// src/pages/Reports/SentDataToCustomersArchive/SentDataToCustomersArchiveUI.jsx

import React from 'react';
import PropTypes from 'prop-types';
import {Button, Grid, Paper, Typography, Skeleton, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Box} from '@mui/material';
import AppComponentHeader from '../../../@crema/core/AppComponentHeader';
import AppGridContainer from '../../../@crema/core/AppGridContainer';
import DataSendArchiveFilterCard from 'components/FilterCard/DataSendArchiveFilterCard';
import FilterListIcon from '@mui/icons-material/FilterList';
import {DataGrid} from '@mui/x-data-grid';

const SentDataToCustomersArchiveUI = ({
  sentDataArchive,
  archiveCount,
  page,
  rowsPerPage,
  loading,
  setSelectedDomain,
  setSelectedCustomer,
  onPageChange,
  onPageSizeChange,
  onFilterChange,
  domains,
  customers,
  handleGenerateReport,
  filters,
  setFilters,
}) => {
  const [isFilterCardVisible, setIsFilterCardVisible] = React.useState(false);

  const toggleFilterCardVisibility = () => {
    setIsFilterCardVisible((prev) => !prev);
  };
  // Function to update domain and customer filters
  const handleDomainChange = (event, newDomain) => {
    setSelectedDomain(newDomain);
    setFilters((prev) => ({
      ...prev,
      domain_id: newDomain ? newDomain.key : '', // Ensure domain ID is being captured
    }));
  };

  const handleCustomerChange = (event, newCustomer) => {
    setSelectedCustomer(newCustomer);
    setFilters((prev) => ({
      ...prev,
      customer_id: newCustomer ? newCustomer.key : '', // Ensure customer ID is being captured
    }));
  };
  // Define columns for the DataGrid
  const columns = [
    {
      field: 'rowNumber',
      headerName: 'Row Number',
      width: 120,
      sortable: false,
      filterable: false,
      valueGetter: (params) => params.api.getRowIndex(params.id) + 1,
    },
    {
      field: 'report_name',
      headerName: 'Report Name',
      width: 250,
      flex: 1,
    },
    {
      field: 'sent_at',
      headerName: 'Sent Date',
      width: 200,
      type: 'date',
      valueGetter: (params) => new Date(params.row.sent_at),
      valueFormatter: (params) => {
        const value = params.value;
        if (value) {
          return new Intl.DateTimeFormat('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          }).format(value);
        }
        return '';
      },
    },
    {
      field: 'customer_name',
      headerName: 'Customer Name',
      width: 200,
      flex: 1,
    },
    {
      field: 'domain_name',
      headerName: 'Sent from Domain',
      width: 200,
      flex: 1,
    },
    {
      field: 'generateReport',
      headerName: 'Generate Report',
      width: 200,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          variant='contained'
          color='primary'
          onClick={() => handleGenerateReport(params.id)}
        >
          Generate
        </Button>
      ),
    },
  ];

  return (
    <>
      <AppComponentHeader title='Sent Data to Customers Archive' />
      <AppGridContainer>
        {/* Toggle Filter Button */}
        <Grid item xs={12}>
          <Button
            variant='contained'
            startIcon={<FilterListIcon />}
            onClick={toggleFilterCardVisibility}
          >
            {isFilterCardVisible ? 'Hide Filters' : 'Show Filters'}
          </Button>
        </Grid>

        {/* Filter Card Section */}
        {isFilterCardVisible && (
          <Grid item xs={12}>
            <DataSendArchiveFilterCard
              handleDomainChange={handleDomainChange} // Now being used
              handleCustomerChange={handleCustomerChange} // Now being used
              setSelectedDomain={setSelectedDomain} // Ensure this is also defined and passed
              setSelectedCustomer={setSelectedCustomer} // Now being used
              domains={domains}
              customers={customers}
              filters={filters}
              setFilters={setFilters}
              onApply={onFilterChange}
              handleDateRangeChange={(newDateRange) => {
                setFilters((prev) => ({
                  ...prev,
                  dateRange: newDateRange,
                }));
              }}
              onClear={() => {
                const clearedFilters = {
                  dateRange: [null, null],
                  domain: '',
                  customer: '',
                };
                setFilters(clearedFilters);
                onFilterChange(clearedFilters);
              }}
              disableDataSource={false}
            />
          </Grid>
        )}

        {/* Data Grid Section */}
        <Grid item xs={12}>
          <Paper sx={{padding: 2, marginTop: 4}}>
            <Typography variant='h6' sx={{mb: 2}}>
              Sent Data Archive List
            </Typography>
            {loading ? (
              <TableContainer 
                component={Paper}
                sx={{
                  maxHeight: 600,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  '& .MuiTable-root': {
                    minWidth: 650,
                  },
                }}
              >
                <Table stickyHeader>
                  <TableHead>
                    <TableRow sx={{'& th': {fontWeight: 600, backgroundColor: 'background.paper'}}}>
                      {columns.map((column) => (
                        <TableCell 
                          key={column.field} 
                          sx={{
                            width: column.width || 'auto',
                            minWidth: column.width || 'auto',
                            backgroundColor: 'background.paper',
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                          }}
                        >
                          {column.headerName}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Array.from({length: Math.min(rowsPerPage, 15)}).map((_, index) => (
                      <TableRow 
                        key={`skeleton-${index}`}
                        sx={{
                          '&:nth-of-type(even)': {
                            backgroundColor: 'action.hover',
                          },
                        }}
                      >
                        {columns.map((column) => (
                          <TableCell 
                            key={`skeleton-${index}-${column.field}`} 
                            sx={{
                              py: 1.5,
                              px: 2,
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                            }}
                          >
                            <Skeleton 
                              variant='rectangular' 
                              height={20} 
                              sx={{
                                borderRadius: 0.5,
                                width: column.width ? '100%' : '80%',
                              }} 
                            />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Box 
                  sx={{
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    p: 2, 
                    borderTop: 1, 
                    borderColor: 'divider',
                    backgroundColor: 'background.paper',
                  }}
                >
                  <Skeleton variant='text' width={120} height={20} />
                  <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                    <Skeleton variant='text' width={100} height={20} />
                    <Skeleton variant='rectangular' width={200} height={36} sx={{borderRadius: 1}} />
                  </Box>
                </Box>
              </TableContainer>
            ) : (
            <DataGrid
              rows={sentDataArchive}
              columns={columns}
                pageSize={rowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
              pagination
              paginationMode='server'
              rowCount={archiveCount}
              onPageChange={(newPage) => onPageChange(null, newPage)}
                onPageSizeChange={(newPageSize) => onPageSizeChange(null, newPageSize)}
              page={page}
              disableSelectionOnClick
              autoHeight
              getRowId={(row) => row.id} // Ensure each row has a unique 'id'
            />
            )}
          </Paper>
        </Grid>
      </AppGridContainer>
    </>
  );
};

SentDataToCustomersArchiveUI.propTypes = {
  sentDataArchive: PropTypes.array.isRequired,
  archiveCount: PropTypes.number.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  loading: PropTypes.bool.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onPageSizeChange: PropTypes.func.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  setSelectedDomain: PropTypes.func.isRequired,
  setSelectedCustomer: PropTypes.func.isRequired, // Make sure this is included

  handleGenerateReport: PropTypes.func.isRequired,
  domains: PropTypes.array.isRequired,
  customers: PropTypes.array.isRequired,
  filters: PropTypes.shape({
    dropdown1: PropTypes.string,
    dropdown2: PropTypes.string,
    dateRange: PropTypes.arrayOf(PropTypes.instanceOf(Date)),
    domain: PropTypes.string,
    customer: PropTypes.string,
  }).isRequired,
  setFilters: PropTypes.func.isRequired,
};

export default SentDataToCustomersArchiveUI;
